import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import * as d3Geo from 'd3-geo';
import * as topojson from 'topojson-client';
import mexicoTopo from '../../assets/dashboard/mexico.json';

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SvgMap = styled.svg`
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 4px 10px rgba(0,0,0,0.3));
`;

const StatePath = styled.path`
  cursor: pointer;
  transition: all 0.2s ease;
  stroke: #1a1a1a;
  stroke-width: 0.5px;

  &:hover {
    filter: brightness(1.2);
    stroke-width: 1.5px;
    stroke: #fff;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  z-index: 10;
  border: 1px solid #BA955E;
  box-shadow: 0 4px 12px rgba(0,0,0,0.5);
  display: ${props => props.visible ? 'block' : 'none'};
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  transform: translate(-50%, -120%);
  white-space: nowrap;

  .name {
    font-weight: 700;
    color: #BA955E;
    margin-bottom: 2px;
  }
  .value {
    font-size: 11px;
    color: #e0e0e0;
  }
`;

const MexicoMap = ({ data = [] }) => {
    const [hoverInfo, setHoverInfo] = useState({ visible: false, x: 0, y: 0, name: '', paid: 0, free: 0 });

    // Convertir TopoJSON a GeoJSON
    const states = useMemo(() => {
        return topojson.feature(mexicoTopo, mexicoTopo.objects.states).features;
    }, []);

    // Proyeccion y generador de caminos
    const { pathGenerator } = useMemo(() => {
        const w = 500;
        const h = 375;

        const projection = d3Geo.geoMercator();
        projection.fitSize([w, h], { type: 'FeatureCollection', features: states });

        const path = d3Geo.geoPath().projection(projection);

        return { pathGenerator: path };
    }, [states]);

    const handleMouseMove = (e, name, paid, free) => {
        const bounds = e.currentTarget.closest('svg').getBoundingClientRect();
        setHoverInfo({
            visible: true,
            x: e.clientX - bounds.left,
            y: e.clientY - bounds.top,
            name,
            paid,
            free
        });
    };

    const handleMouseOut = () => {
        setHoverInfo({ ...hoverInfo, visible: false });
    };

    const PAID_COLOR = '#D6AB72';
    const FREE_COLOR = '#44BF0A';
    const EMPTY_COLOR = '#888888';

    return (
        <MapContainer>
            <SvgMap viewBox={`0 0 500 375`} preserveAspectRatio="xMidYMid meet">
                {states.map((feature, i) => {
                    const stateCode = feature.properties.state_code;
                    const stateName = feature.properties.state_name;
                    const dataItem = data.find(d => d.id === stateCode || d.name === stateName);

                    const paid = dataItem ? (dataItem.paidUsers || 0) : 0;
                    const free = dataItem ? (dataItem.freeUsers || 0) : 0;

                    let fillColor = EMPTY_COLOR;
                    if (dataItem && (paid > 0 || free > 0)) {
                        fillColor = paid > free ? PAID_COLOR : FREE_COLOR;
                    }

                    return (
                        <StatePath
                            key={i}
                            d={pathGenerator(feature)}
                            fill={fillColor}
                            onMouseMove={(e) => handleMouseMove(e, stateName, paid, free)}
                            onMouseOut={handleMouseOut}
                        />
                    );
                })}
            </SvgMap>
            <Tooltip visible={hoverInfo.visible} x={hoverInfo.x} y={hoverInfo.y}>
                <div className="name">{hoverInfo.name}</div>
                <div className="value">
                    <span style={{ color: PAID_COLOR }}>De Pago: {hoverInfo.paid.toLocaleString()}</span>
                    <br />
                    <span style={{ color: FREE_COLOR }}>Gratuitas: {hoverInfo.free.toLocaleString()}</span>
                </div>
            </Tooltip>
        </MapContainer>
    );
};

export default MexicoMap;
